import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CrudStyles } from '../shared/Crud';
import configurationService from '../../services/configurationService';
import {
    CONFIGURATION_INTEGER_RANGES,
    CONFIGURATION_TYPES,
    configurationDraftValue,
    isSameConfigurationValue,
} from '../../constants/systemConfiguration';
import {
    BooleanControl,
    FieldError,
    Hint,
    InlineStatus,
    ParameterControls,
    ParameterDescription,
    ParameterForm,
    ParameterInput,
    ParameterItem,
    ParameterKey,
    ParameterList,
} from './styles';

const { Button } = CrudStyles;

function fieldId(key) {
    return `configuracao-${key}`;
}

function valueToSend(item, draft) {
    if (item.type === CONFIGURATION_TYPES.BOOLEAN) {
        return configurationDraftValue({ type: item.type, value: draft });
    }
    return draft == null ? '' : String(draft);
}

function ParameterEditor({
    item,
    draft,
    error,
    saved,
    saving,
    canEdit,
    onDraftChange,
    onSave,
}) {
    const id = fieldId(item.key);
    const range = item.type === CONFIGURATION_TYPES.INTEGER
        ? CONFIGURATION_INTEGER_RANGES[item.key]
        : null;
    const unchanged = isSameConfigurationValue(item, draft);
    const disabled = !canEdit || saving;

    return (
        <ParameterItem>
            <ParameterForm noValidate onSubmit={(event) => onSave(event, item)}>
                <ParameterDescription htmlFor={id}>{item.description}</ParameterDescription>
                <ParameterKey>{item.key}</ParameterKey>
                {range ? (
                    <Hint>
                        Informe um número inteiro entre {range.min} e {range.max}.
                    </Hint>
                ) : null}
                <ParameterControls>
                    {item.type === CONFIGURATION_TYPES.BOOLEAN ? (
                        <BooleanControl>
                            <input
                                id={id}
                                type="checkbox"
                                role="switch"
                                aria-checked={draft === 'true'}
                                checked={draft === 'true'}
                                disabled={disabled}
                                onChange={(event) => onDraftChange(
                                    item.key,
                                    event.target.checked ? 'true' : 'false',
                                )}
                            />
                            <span>{draft === 'true' ? 'Sim' : 'Não'}</span>
                        </BooleanControl>
                    ) : (
                        <ParameterInput
                            id={id}
                            type={item.type === CONFIGURATION_TYPES.INTEGER ? 'number' : 'text'}
                            inputMode={item.type === CONFIGURATION_TYPES.INTEGER ? 'numeric' : 'text'}
                            step={item.type === CONFIGURATION_TYPES.INTEGER ? '1' : undefined}
                            value={draft}
                            disabled={disabled}
                            autoComplete="off"
                            $wide={item.type !== CONFIGURATION_TYPES.INTEGER}
                            $hasError={Boolean(error)}
                            aria-invalid={Boolean(error)}
                            onChange={(event) => onDraftChange(item.key, event.target.value)}
                        />
                    )}
                    {canEdit ? (
                        <Button
                            type="submit"
                            variant="primary"
                            aria-label={`Salvar ${item.key}`}
                            disabled={disabled || unchanged}
                        >
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    ) : null}
                    {saved ? <InlineStatus role="status">{saved}</InlineStatus> : null}
                </ParameterControls>
                {error ? <FieldError role="alert">{error}</FieldError> : null}
            </ParameterForm>
        </ParameterItem>
    );
}

export default function SystemParametersSection() {
    const { user } = useAuth();
    const canEdit = (user?.roles || []).includes('ROLE_TRANSPORT_COMPANY');
    const [items, setItems] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [errors, setErrors] = useState({});
    const [saved, setSaved] = useState({});
    const [saving, setSaving] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [reloadToken, setReloadToken] = useState(0);
    const savingRef = useRef({});

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError('');
        configurationService.listConfigurations()
            .then((list) => {
                if (!active) return;
                setItems(list);
                setDrafts(Object.fromEntries(
                    list.map((item) => [item.key, configurationDraftValue(item)]),
                ));
                setErrors({});
                setSaved({});
            })
            .catch((error) => {
                if (!active) return;
                setItems([]);
                setLoadError(error?.message || 'Não foi possível carregar os parâmetros.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [reloadToken]);

    const handleDraftChange = (key, value) => {
        setDrafts((current) => ({ ...current, [key]: value }));
        setErrors((current) => ({ ...current, [key]: '' }));
        setSaved((current) => ({ ...current, [key]: '' }));
    };

    const handleSave = async (event, item) => {
        event.preventDefault();
        if (savingRef.current[item.key] || !canEdit) return;
        const draft = drafts[item.key];
        if (isSameConfigurationValue(item, draft)) return;

        savingRef.current[item.key] = true;
        setSaving((current) => ({ ...current, [item.key]: true }));
        setErrors((current) => ({ ...current, [item.key]: '' }));
        setSaved((current) => ({ ...current, [item.key]: '' }));

        try {
            const updated = await configurationService.patchConfiguration(item.key, {
                value: valueToSend(item, draft),
            });
            setItems((current) => current.map((entry) => (
                entry.key === updated.key ? updated : entry
            )));
            setDrafts((current) => ({
                ...current,
                [updated.key]: configurationDraftValue(updated),
            }));
            setSaved((current) => ({ ...current, [updated.key]: 'Salvo.' }));
        } catch (error) {
            const message = typeof error?.message === 'string' && error.message.trim()
                ? error.message
                : 'Não foi possível salvar o parâmetro.';
            setErrors((current) => ({ ...current, [item.key]: message }));
        } finally {
            savingRef.current[item.key] = false;
            setSaving((current) => ({ ...current, [item.key]: false }));
        }
    };

    if (loading) {
        return <Hint>Carregando parâmetros...</Hint>;
    }

    if (loadError) {
        return (
            <>
                <FieldError role="alert">{loadError}</FieldError>
                <div>
                    <Button type="button" variant="outline" onClick={() => setReloadToken((token) => token + 1)}>
                        Tentar novamente
                    </Button>
                </div>
            </>
        );
    }

    if (items.length === 0) {
        return <Hint>Nenhum parâmetro disponível.</Hint>;
    }

    return (
        <>
            <Hint>
                {canEdit
                    ? 'A descrição e a chave são definidas pelo sistema. Só o valor pode ser alterado.'
                    : 'Você pode consultar estes parâmetros. Somente a empresa de transporte pode alterá-los.'}
            </Hint>
            <ParameterList>
                {items.map((item) => (
                    <ParameterEditor
                        key={item.key}
                        item={item}
                        draft={drafts[item.key] ?? ''}
                        error={errors[item.key]}
                        saved={saved[item.key]}
                        saving={Boolean(saving[item.key])}
                        canEdit={canEdit}
                        onDraftChange={handleDraftChange}
                        onSave={handleSave}
                    />
                ))}
            </ParameterList>
        </>
    );
}
